import { fabric } from 'fabric';

import { AGENT_USER_IDS } from './agent-ids';
import { FabricObject } from './board-items-manager';

// Re-exported so existing importers of `AGENT_USER_IDS` from this module keep working;
// the source of truth is the dependency-free ./agent-ids.
export { AGENT_USER_IDS };

/**
 * "Draw-on" entrance animation for objects the AI tutor ("Luna") writes to the
 * board: instead of popping in, agent content reveals left-to-right like it's
 * being written. Two render paths:
 *   - Formulas/equations are HTML (KaTeX) — animated via the `agent-drawon` CSS
 *     class on the element (see board.item.ts onHtmlAdded + styles.scss).
 *   - Text / scribbles / shapes are fabric canvas objects — animated here with a
 *     growing clipPath (animateCanvasDrawOn).
 *
 * Gating: only the agent's own objects, and only AFTER the initial board backfill
 * has settled — otherwise a page refresh / board switch would re-animate the
 * whole board. The live gate is (re)armed on each board open (openAgentLiveGate).
 */

// Entrance duration for canvas objects (text/scribbles/shapes). Kept in step with
// the equation reveal (styles.scss .agent-drawon) so the board feels consistent.
const DRAWON_MS = 1700;

// When the agent drops several objects in one batch, animating them all at once is
// hard to follow. Instead we stagger their starts so they reveal one-by-one (with
// slight overlap, so it doesn't feel sluggish). ~70% of DRAWON_MS means object N+1
// starts while object N is still finishing its tail.
const STAGGER_MS = Math.round(DRAWON_MS * 0.7);

// A large batch shouldn't take forever to fully reveal: cap the *last* object's
// start delay. Past this cap the per-item stagger shrinks so the whole sequence
// fits within the budget (objects then overlap more, but still read in order).
const MAX_TOTAL_STAGGER_MS = 6000;

/**
 * Per-object start delay (ms) for a staggered batch reveal. `index` is the object's
 * position in the (already-ordered) batch; `count` is the batch size. Index 0 is
 * always 0 (single-object behaviour is unchanged). For large batches the per-item
 * delay shrinks so the last object starts no later than MAX_TOTAL_STAGGER_MS.
 */
export function drawOnStaggerDelay(index: number, count: number): number {
  if (index <= 0) {
    return 0;
  }
  const lastIndex = Math.max(count - 1, 1);
  const perItem = Math.min(STAGGER_MS, MAX_TOTAL_STAGGER_MS / lastIndex);
  return Math.round(index * perItem);
}

// --- HTML/equation stagger scheduler ------------------------------------------
// The canvas path staggers a batch via an explicit index (drawOnStaggerDelay), but
// HTML items (KaTeX equations) each animate independently when their element mounts
// (board.item.ts maybePlayAgentEntrance) — so a batch of equations would all start
// together. We can't see the batch there, but the mounts arrive in a tight burst,
// so we assign each entrance the next slot in the current burst (reading order ≈
// mount order ≈ top-to-bottom in a section). A pause longer than the reset gap
// starts a fresh sequence, so a later section's equations don't inherit a big delay.
const BURST_RESET_GAP_MS = 350;
let burstLastAt = 0;
let burstIndex = 0;

/** Next staggered start delay (ms) for an HTML agent-entrance, so a burst of
 * equations reveals one-by-one instead of all at once. Resets after a quiet gap. */
export function nextAgentEntranceDelayMs(): number {
  const now = Date.now();
  burstIndex = now - burstLastAt > BURST_RESET_GAP_MS ? 0 : burstIndex + 1;
  burstLastAt = now;
  return Math.min(burstIndex * STAGGER_MS, MAX_TOTAL_STAGGER_MS);
}

// The initial sync backfill lands immediately when a board opens; treat anything
// added before this timestamp as backfill (don't animate). Armed per board open.
let agentLiveAt = Number.POSITIVE_INFINITY;

/** (Re)arm the live gate: animate agent writes that arrive `delayMs` after now.
 * Called whenever a board is (re)opened so its backfill isn't animated. */
export function openAgentLiveGate(delayMs = 1500): void {
  agentLiveAt = Date.now() + delayMs;
}

/** Animate this object's entrance? Agent-authored AND a live write (not backfill). */
export function shouldAnimateAgentEntrance(userId: string | undefined | null): boolean {
  return !!userId && AGENT_USER_IDS.has(userId) && Date.now() >= agentLiveAt;
}

/**
 * Reveal a fabric canvas object left-to-right via a growing clipPath. Non-mutating:
 * the object's own geometry is untouched and any prior clipPath is restored on
 * completion, so erasability/selection are unaffected.
 */
export function animateCanvasDrawOn(
  canvas: { requestRenderAll: () => void },
  obj: FabricObject,
  delayMs = 0,
): void {
  const w = (obj.width as number) ?? 0;
  const h = (obj.height as number) ?? 0;
  if (!w || !h) {
    return;
  }
  const prevClip = obj.clipPath;
  // Object caching renders the object from a cached bitmap, which would NOT pick
  // up the per-frame clipPath change — disable it for the duration so the reveal
  // actually animates, then restore.
  const prevCaching = (obj as { objectCaching?: boolean }).objectCaching;
  (obj as { objectCaching?: boolean }).objectCaching = false;
  // clipPath coords are relative to the object's center; start as a sliver at the
  // left edge and grow rightward to reveal the object.
  const clip = new fabric.Rect({
    originX: 'left',
    originY: 'center',
    left: -w / 2,
    top: 0,
    width: 1,
    height: h,
  });
  // Apply the sliver clip immediately so a staggered object stays hidden (clipped
  // to a 1px sliver) until its turn — it must NOT pop in fully-drawn before the
  // delay elapses.
  obj.clipPath = clip;
  (obj as { dirty?: boolean }).dirty = true;
  canvas.requestRenderAll();

  const start = (): void => {
    fabric.util.animate({
      startValue: 1,
      endValue: w,
      duration: DRAWON_MS,
      easing: (fabric.util as any).ease.easeOutCubic,
      onChange: (value: number) => {
        clip.set('width', value);
        (obj as { dirty?: boolean }).dirty = true;
        canvas.requestRenderAll();
      },
      onComplete: () => {
        obj.clipPath = prevClip;
        (obj as { objectCaching?: boolean }).objectCaching = prevCaching;
        (obj as { dirty?: boolean }).dirty = true;
        canvas.requestRenderAll();
      },
    });
  };

  if (delayMs > 0) {
    setTimeout(start, delayMs);
  } else {
    start();
  }
}
